import {
  Link, LinkAsButton, Spinner,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import React from 'react';
import { useSelectorMachineContext } from '@printdeal/product-selector';
import { useQuery } from '@tanstack/react-query';
import { ServiceFactory } from '../../../../services/ServiceFactory';

const surfaceDependencyService = ServiceFactory.createSurfaceDependencyService();

interface Props {
  productKey: string,
  productVersion?: number,
}

const TechnicalDrawing = ({ productKey, productVersion }: Props) => {
  const intl = useIntl();
  const selection = useSelectorMachineContext((state) => state.context.selection);
  const { data, status, refetch } = useQuery({
    queryKey: [productKey, productVersion, selection],
    refetchInterval: (drawingData) => (drawingData.state.data?.status === 'READY' ? false : 3000),
    queryFn: () => surfaceDependencyService.getTechnicalDrawingPdfUrl(
      productKey,
      selection,
      intl.locale.toLocaleLowerCase(),
      productVersion,
    ),
  });

  if (status === 'pending' || data?.status === 'IN_PROGRESS') {
    return (
      <div className="pd-flex pd-items-center pd-gap-2">
        <Spinner
          variant="primary"
          screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
        />
        <p className="pd-animate-pulse pd-text-dim-500">
          <FormattedMessage
            id="product.show.technicaldrawing.loading"
            defaultMessage="Retrieving technical drawings..."
          />
        </p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <p className="pd-italic pd-text-error-500">
        <FormattedMessage
          id="product.show.technicaldrawing.error"
          defaultMessage="An error occurred while retrieving the technical drawings."
        />
        {' '}
        <LinkAsButton
          className="pd-italic"
          type="button"
          onClick={() => refetch()}
        >
          <FormattedMessage id="product.show.technicaldrawing.retry" defaultMessage="Try again" />
        </LinkAsButton>
      </p>
    );
  }

  if (data?.status === 'READY') {
    return (
      <Link
        variant="secondary"
        target="_blank"
        href={data.resultUrl}
        iconBefore="download"
      >
        <FormattedMessage id="product.show.technicaldrawing" defaultMessage="Download technical drawings" />
      </Link>
    );
  }

  return null;
};

export default TechnicalDrawing;
